'use client';

/**
 * ShortsPlayer — inline YouTube Shorts player with OUR OWN controls.
 *
 * YouTube's native iframe renders its mute/CC/fullscreen chrome at positions we
 * can't touch (cross-origin). To place controls where the design wants them
 * (bottom-right, grouped next to fullscreen), we suppress the native controls
 * (`controls=0`) and drive the player through the IFrame Player API via
 * react-youtube:
 *   - Mute       → player.mute() / unMute() / isMuted()
 *   - Captions   → the (undocumented) captions module setOption — best-effort
 *   - Fullscreen → the browser Fullscreen API on OUR container, so our controls
 *                  stay visible in fullscreen (fullscreening the raw iframe would
 *                  hide them, leaving no controls at all under controls=0).
 *
 * Used only by ShortsShelf for the playing state of a 9:16 card. Regular 16:9
 * videos keep the plain native-controls iframe (MediaHeroCard / RelatedMediaPanel).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import YouTube, { type YouTubeProps } from 'react-youtube';
import { Volume2, VolumeX, Captions, Maximize, Minimize } from 'lucide-react';
import { cn } from '@/lib/utils';

/** The subset of the YouTube IFrame Player API we drive. We convert to this
 *  typed shape at the onReady boundary so field access never touches `any`. */
interface YTPlayer {
  mute(): void;
  unMute(): void;
  isMuted(): boolean;
  playVideo(): void;
  getPlayerState(): number;
  loadModule(module: string): void;
  setOption(module: string, option: string, value: unknown): void;
  getIframe(): HTMLIFrameElement;
}

/** YT.PlayerState.PLAYING */
const PLAYING = 1;

const PLAYER_OPTS: YouTubeProps['opts'] = {
  width: '100%',
  height: '100%',
  playerVars: {
    autoplay: 1,
    controls: 0, // suppress native chrome — we render our own overlay
    rel: 0,
    modestbranding: 1,
    playsinline: 1,
    fs: 0, // our fullscreen button owns this
    cc_load_policy: 0, // captions start OFF; toggled via setOption
    iv_load_policy: 3,
    enablejsapi: 1,
  },
};

// Both module names are tried: the legacy player exposes captions as 'captions',
// the HTML5 player as 'cc'. Setting the option on both is the robust toggle.
const CC_MODULES = ['captions', 'cc'] as const;

export function ShortsPlayer({
  videoId,
  title,
}: {
  videoId: string;
  title?: string | null;
}): React.ReactElement {
  const playerRef = useRef<YTPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [muted, setMuted] = useState(false);
  const [showCC, setShowCC] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const onReady = useCallback<NonNullable<YouTubeProps['onReady']>>((event) => {
    const p = event.target as unknown as YTPlayer;
    playerRef.current = p;
    // Play is user-initiated (tapping the card), so autoplay-with-sound is
    // normally allowed; if the browser still blocks it, rescue playback muted so
    // the short always plays (mirrors the podcast player's proven fallback).
    p.playVideo();
    window.setTimeout(() => {
      if (p.getPlayerState() !== PLAYING) {
        p.mute();
        setMuted(true);
        p.playVideo();
      }
    }, 800);
    // Preload the caption modules so the first CC tap has them available.
    CC_MODULES.forEach((m) => {
      try {
        p.loadModule(m);
      } catch {
        /* module may not exist on this player build — CC stays best-effort */
      }
    });
  }, []);

  const toggleMute = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    if (p.isMuted()) {
      p.unMute();
      setMuted(false);
    } else {
      p.mute();
      setMuted(true);
    }
  }, []);

  const toggleCC = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    const next = !showCC;
    setShowCC(next);
    const track = next ? { languageCode: 'en' } : {};
    CC_MODULES.forEach((m) => {
      try {
        p.loadModule(m);
        p.setOption(m, 'track', track);
      } catch {
        /* undocumented API — best-effort per the accepted tradeoff */
      }
    });
  }, [showCC]);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative size-full bg-black [&:fullscreen]:h-screen [&:fullscreen]:w-screen"
    >
      <YouTube
        videoId={videoId}
        className="absolute inset-0 size-full"
        iframeClassName="size-full border-0"
        opts={PLAYER_OPTS}
        onReady={onReady}
        title={title || 'YouTube Short'}
      />
      {/* Controls overlay: pass-through everywhere except the button cluster, so
          tapping the video still play/pauses via the chromeless native player. */}
      <div className="pointer-events-none absolute inset-0 z-10">
        <div className="pointer-events-auto absolute bottom-[10px] right-[10px] flex items-center gap-[6px]">
          <ControlButton label={muted ? 'Unmute' : 'Mute'} onClick={toggleMute}>
            {muted ? (
              <VolumeX className="size-[15px]" />
            ) : (
              <Volume2 className="size-[15px]" />
            )}
          </ControlButton>
          <ControlButton
            label={showCC ? 'Hide captions' : 'Show captions'}
            onClick={toggleCC}
            active={showCC}
          >
            <Captions className="size-[15px]" />
          </ControlButton>
          <ControlButton
            label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <Minimize className="size-[15px]" />
            ) : (
              <Maximize className="size-[15px]" />
            )}
          </ControlButton>
        </div>
      </div>
    </div>
  );
}

function ControlButton({
  label,
  onClick,
  active = false,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'flex size-[30px] items-center justify-center rounded-full backdrop-blur-[2px] transition-colors',
        active
          ? 'bg-white text-[#31364e]'
          : 'bg-black/55 text-white hover:bg-black/70'
      )}
    >
      {children}
    </button>
  );
}

export default ShortsPlayer;
